import { i18n } from '@/i18n';
import store from '@/store';
import { initState } from '@/store/stateinit';
import {
    loadStripe,
    Stripe,
    StripeElements,
    StripePaymentElement,
    StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { Action, getModule, Module, Mutation, MutationAction, VuexModule } from 'vuex-module-decorators';
import { QuoteAddress, Region, StripeConfig } from '../models';
import cart from './cart';
import checkout from './checkout';

@Module({
    namespaced: true,
    name: 'stripe',
    store,
    dynamic: true,
})
class StripeModule extends VuexModule {
    public config: StripeConfig = initState('stripe');
    public stripe: Stripe | null = null;
    public selected_method_code: string | null = null;
    private stripeElements: StripeElements | null = null;
    private stripePaymentElement: StripePaymentElement | null = null;

    get grandTotal() {
        const grandTotal = cart.totals.total_segments.find((total) => total.code === 'grand_total');
        return grandTotal ? grandTotal.value : 0;
    }

    get stripePaymentOptions(): StripePaymentElementOptions {
        const state = ['string', 'number'].includes(typeof checkout.billingAddress.region)
            ? checkout.billingAddress.region.toString()
            : (checkout.billingAddress.region as Region).region_code;

        return {
            layout: this.config.form_layout === '1' ? 'accordion' : 'tabs',
            defaultValues: {
                billingDetails: {
                    phone: checkout.billingAddress.telephone,
                    name: `${checkout.billingAddress.firstname} ${checkout.billingAddress.lastname}`,
                    email: checkout.email,
                    address: {
                        city: checkout.billingAddress.city,
                        country: checkout.billingAddress.country_id,
                        postal_code: checkout.billingAddress.postcode,
                        state,
                        line1: checkout.billingAddress.street.join(' '),
                    },
                },
            },
        };
    }

    @Mutation
    public setSelectedMethodCode(value: string | null) {
        this.selected_method_code = value;
    }

    @Mutation
    public setStripe(stripe: Stripe | null) {
        this.stripe = stripe;
    }

    @Mutation
    public setStripeElements(stripeElements: StripeElements) {
        this.stripeElements = stripeElements;
    }

    @Mutation
    public setStripePaymentElement(stripePaymentElement: StripePaymentElement) {
        this.stripePaymentElement = stripePaymentElement;
    }

    @Action
    public async getStripeElements() {
        if (!this.stripe) {
            this.setStripe(await loadStripe(this.config.publishable_api_key));
        }

        if (this.stripe && !this.stripeElements) {
            const stripeElements: StripeElements = this.stripe.elements({
                amount: Math.floor(this.grandTotal * 100), // Stripe format
                appearance: {
                    theme: 'stripe',
                },
                currency: store.state.options.currency.code.toLowerCase(),
                locale: store.state.options.locale,
                mode: 'payment',
                paymentMethodCreation: 'manual',
                customerSessionClientSecret: this.config.customer_session_secret,
            });

            this.setStripeElements(stripeElements);
        }

        return this.stripeElements;
    }

    @Action
    public async getStripePaymentElement() {
        if (!this.stripeElements) {
            await this.getStripeElements();
        }

        if (this.stripeElements && !this.stripePaymentElement) {
            this.setStripePaymentElement(this.stripeElements.create('payment', this.stripePaymentOptions));
        }

        return this.stripePaymentElement;
    }

    @Action
    public async validateStripePaymentElement() {
        if (this.stripeElements) {
            return this.stripeElements
                .submit()
                .then((result) => {
                    if (result.error) {
                        return { validationResult: false, validationMessage: result.error.message || '' };
                    }

                    return { validationResult: true, validationMessage: '' };
                })
                .catch(() => {
                    return {
                        validationResult: false,
                        validationMessage: i18n.t('Service unavailable, retry later').toString(),
                    };
                });
        }

        return { validationResult: false, validationMessage: '' };
    }

    @Action
    public async updateStripeBillingAddress(billingAddress: QuoteAddress) {
        if (this.config.is_enabled) {
            if (this.stripeElements) {
                const paymentElement = this.stripeElements.getElement('payment');

                if (paymentElement) {
                    paymentElement.update(this.stripePaymentOptions);
                }
            }

            // FIXME: this call always throws error. It has something to do with this specific line:
            //  `vendor/stripe/module-payments/Api/Service.php:643`. This logic was used to update the payment methods
            //  list. This is currently used only to show the correct images
            // this.stripeConfig.payment_methods = JSON.parse(await stripeGetPaymentMethods(billingAddress) || '');
        }
    }
}

export default getModule(StripeModule);
